// Shared UI pieces: cards (with table-view twin), stat tiles, status badges, JSON viewer.

import type { HealthStatus, SampleLabel } from '@growth/client';
import { type ReactNode, useState } from 'react';
import { Sparkline } from './charts.tsx';

// ------------------------------------------------------------------ icons (status always ships icon + label)

export function Icon({ name }: { name: 'good' | 'info' | 'warning' | 'critical' | 'copy' }) {
  const common = { className: 'icon', width: 12, height: 12, viewBox: '0 0 12 12', 'aria-hidden': true } as const;
  switch (name) {
    case 'good':
      return (
        <svg {...common}>
          <circle cx="6" cy="6" r="6" fill="var(--good)" />
          <path d="M3.3 6.2 5.2 8 8.8 4.2" stroke="#fff" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'warning':
      return (
        <svg {...common}>
          <path d="M6 .8 11.4 10.6H.6Z" fill="var(--warning)" />
          <path d="M6 4.3v3" stroke="#0b0b0b" strokeWidth="1.4" strokeLinecap="round" />
          <circle cx="6" cy="9" r=".8" fill="#0b0b0b" />
        </svg>
      );
    case 'critical':
      return (
        <svg {...common}>
          <circle cx="6" cy="6" r="6" fill="var(--critical)" />
          <path d="M4 4l4 4M8 4 4 8" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
    case 'copy':
      return (
        <svg {...common}>
          <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" fill="none" stroke="currentColor" />
          <path d="M8.5 2.5v-.5A1.5 1.5 0 0 0 7 .5H2A1.5 1.5 0 0 0 .5 2v5A1.5 1.5 0 0 0 2 8.5h.5" fill="none" stroke="currentColor" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="6" cy="6" r="5.4" fill="none" stroke="var(--muted)" strokeWidth="1.2" />
          <path d="M6 5.3v3.2" stroke="var(--muted)" strokeWidth="1.3" strokeLinecap="round" />
          <circle cx="6" cy="3.5" r=".75" fill="var(--muted)" />
        </svg>
      );
  }
}

const HEALTH: Record<HealthStatus, { icon: 'good' | 'info' | 'warning' | 'critical'; label: string }> = {
  ok: { icon: 'good', label: 'OK' },
  info: { icon: 'info', label: 'Info' },
  warn: { icon: 'warning', label: 'Aviso' },
  error: { icon: 'critical', label: 'Error' },
};

export function StatusBadge({ status }: { status: HealthStatus }) {
  const s = HEALTH[status];
  return (
    <span className="badge">
      <Icon name={s.icon} />
      {s.label}
    </span>
  );
}

const SAMPLE: Record<SampleLabel, string> = {
  insufficient: 'Muestra insuficiente',
  small: 'Muestra pequeña',
  medium: 'Muestra media',
  large: 'Muestra grande',
};

export function SampleBadge({ sample, n }: { sample: SampleLabel; n?: number }) {
  return (
    <span className="badge" title="insuficiente < 30 · pequeña < 100 · media < 1000 · grande ≥ 1000">
      {sample === 'insufficient' && <Icon name="warning" />}
      {SAMPLE[sample]}
      {n !== undefined ? ` · n=${n}` : ''}
    </span>
  );
}

// ------------------------------------------------------------------ cards

export interface TableData {
  columns: { label: string; numeric?: boolean }[];
  rows: (ReactNode | string | number | null)[][];
}

export function DataTable({ data, onRowClick, selected, compact }: { data: TableData; onRowClick?: (i: number) => void; selected?: number | null; compact?: boolean }) {
  return (
    <div className="table-wrap">
      <table className={`data${compact ? ' compact' : ''}`}>
        <thead>
          <tr>
            {data.columns.map((c) => (
              <th key={c.label} className={c.numeric ? 'num' : undefined} scope="col">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => (
            <tr key={i} className={`${onRowClick ? 'clickable' : ''}${selected === i ? ' selected' : ''}`} onClick={onRowClick ? () => onRowClick(i) : undefined}>
              {r.map((v, k) => (
                <td key={k} className={data.columns[k]?.numeric ? 'num' : undefined}>
                  {v === null || v === undefined ? '—' : v}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Card({ title, subtitle, actions, children, className }: { title?: ReactNode; subtitle?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`card${className ? ` ${className}` : ''}`}>
      {(title || actions) && (
        <div className="card-head">
          <div>
            {title && <h2>{title}</h2>}
            {subtitle && <p>{subtitle}</p>}
          </div>
          {actions && <div className="card-actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/** A chart in a <figure> with its accessible twin: the table view toggle. */
export function ChartCard({ title, subtitle, table, children, actions, stale }: { title: string; subtitle?: ReactNode; table?: TableData; children: ReactNode; actions?: ReactNode; stale?: boolean }) {
  const [asTable, setAsTable] = useState(false);
  return (
    <figure className="card">
      <div className="card-head">
        <div>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <div className="card-actions">
          {actions}
          {table && (
            <button type="button" className="btn small ghost" aria-pressed={asTable} onClick={() => setAsTable((v) => !v)}>
              {asTable ? 'Ver gráfico' : 'Ver tabla'}
            </button>
          )}
        </div>
      </div>
      <div className={`loading-frame${stale ? ' stale' : ''}`}>{asTable && table ? <DataTable data={table} /> : children}</div>
    </figure>
  );
}

// ------------------------------------------------------------------ stat tile

export interface TileDelta {
  text: string;
  /** good/bad already combines direction and whether up is good. */
  tone: 'good' | 'bad' | 'neutral';
  vs: string;
}

export function StatTile({ label, value, sub, delta, trend, placeholder, compact }: { label: string; value: string; sub?: ReactNode; delta?: TileDelta; trend?: number[]; placeholder?: boolean; compact?: boolean }) {
  return (
    <div className={`tile${placeholder ? ' placeholder' : ''}`}>
      <span className="tile-label">{label}</span>
      <div className="tile-main">
        <span className={`tile-value${compact ? ' text' : ''}`}>{value}</span>
        {trend && <Sparkline values={trend} />}
      </div>
      <div className="tile-foot tile-sub">
        {delta && (
          <span className={`delta ${delta.tone === 'good' ? 'up' : delta.tone === 'bad' ? 'down' : 'flat'}`} title={`vs ${delta.vs}`}>
            {delta.text}{' '}
          </span>
        )}
        {sub}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ states

export function ErrorBox({ error, onRetry }: { error: Error | string; onRetry?: () => void }) {
  return (
    <div className="error-box row" role="alert">
      <Icon name="critical" />
      <span style={{ flex: 1 }}>{typeof error === 'string' ? error : error.message}</span>
      {onRetry && (
        <button type="button" className="btn small" onClick={onRetry}>
          Reintentar
        </button>
      )}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function CopyButton({ text, label = 'Copiar' }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="btn small"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        } catch {
          setDone(false);
        }
      }}
    >
      <Icon name="copy" />
      {done ? 'Copiado' : label}
    </button>
  );
}

export function JsonView({ value }: { value: unknown }) {
  const text = JSON.stringify(value, null, 2);
  return (
    <div className="stack">
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <CopyButton text={text} label="Copiar JSON" />
      </div>
      <pre className="json">{text}</pre>
    </div>
  );
}

export function Secret({ value }: { value: string }) {
  return (
    <span className="secret">
      <code title={value}>{value}</code>
      <CopyButton text={value} />
    </span>
  );
}

export function download(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
